import { z } from 'zod'
import { Model } from './architecture'
import type { Market, Markets } from '../shared/domain/Market'
import type { Affiliate } from '../shared/domain/Affiliate'

export type PriceTypes = (typeof ALLOWED_PRICE_TYPES)[number]
export const ALLOWED_PRICE_TYPES = [
  'allIn',
  'allInDaily',
  'net',
  'netSubtotal',
  'gross',
  'netWithFees',
  'netWithFeesDaily',
] as const

export const PriceFormatValidation = z.object({
  value: z.enum(ALLOWED_PRICE_TYPES, { required_error: '[PriceFormat] value is required' }),
})

export type KindOfAmount = 'total' | 'perNight'

export class PriceType extends Model {
  static readonly #marketToShowNightNetPrices = ['us', 'ca'] as const
  static readonly #marketToShowAllInPrices = ['co', 'mx', 'br'] as const
  static readonly #marketToShowAllInDailyPrices = ['au'] as const

  static readonly #USDefaultAffiliateFormat: Partial<Record<Markets, PriceTypes>> = {
    us: 'allIn',
  }

  static readonly #marketTrivagoPriceFormat: Partial<Record<Markets, PriceTypes>> = {
    us: 'netWithFeesDaily',
    ca: 'netWithFeesDaily',
    br: 'netSubtotal',
    mx: 'netSubtotal',
    za: 'gross',
  }

  static readonly #marketTripadvisorPriceFormat: Partial<Record<Markets, PriceTypes>> = {
    us: 'netWithFeesDaily',
    ca: 'netWithFeesDaily',
    fr: 'allIn',
    au: 'allIn',
    br: 'netSubtotal',
    mx: 'netSubtotal',
  }

  static readonly #marketGhaPriceFormat: Partial<Record<Markets, PriceTypes>> = {
    us: 'netWithFeesDaily',
    ca: 'netWithFeesDaily',
  }

  static readonly priceTypesInformation: Record<
    PriceTypes,
    {
      featuredPrice: KindOfAmount
      vatsIncluded: boolean
      taxIncluded: boolean
      feesIncluded: boolean
    }
  > = {
    allIn: {
      featuredPrice: 'total',
      vatsIncluded: true,
      taxIncluded: true,
      feesIncluded: false,
    },
    allInDaily: {
      featuredPrice: 'perNight',
      vatsIncluded: true,
      taxIncluded: true,
      feesIncluded: false,
    },
    net: {
      featuredPrice: 'perNight',
      vatsIncluded: true,
      taxIncluded: false,
      feesIncluded: false,
    },
    netSubtotal: {
      featuredPrice: 'total',
      vatsIncluded: false,
      taxIncluded: false,
      feesIncluded: false,
    },
    gross: {
      featuredPrice: 'total',
      vatsIncluded: true,
      taxIncluded: false,
      feesIncluded: false,
    },
    netWithFees: {
      featuredPrice: 'total',
      vatsIncluded: false,
      taxIncluded: false,
      feesIncluded: true,
    },
    netWithFeesDaily: {
      featuredPrice: 'perNight',
      vatsIncluded: false,
      taxIncluded: false,
      feesIncluded: true,
    },
  }

  static create({ value }: z.infer<typeof PriceFormatValidation>) {
    PriceFormatValidation.parse({ value })
    return new PriceType(value)
  }

  static createOrUndefinedFromCookie({
    value,
  }: {
    value:
      | 'all_in'
      | 'all_in_daily'
      | 'net'
      | 'gross'
      | 'trivago_net'
      | 'net_with_fees'
      | 'net_with_fees_daily'
      | undefined
  }) {
    try {
      if (!value) throw new Error('value is required')

      const valueNormalized = {
        all_in: 'allIn',
        all_in_daily: 'allInDaily',
        net: 'net',
        gross: 'gross',
        trivago_net: 'netSubtotal',
        net_with_fees: 'netWithFees',
        net_with_fees_daily: 'netWithFeesDaily',
      } as const

      return PriceType.create({ value: valueNormalized[value] })
    } catch {
      return undefined
    }
  }

  static fromAffiliatePriceType({ affiliate, market }: { affiliate: Affiliate; market: Market }) {
    if (affiliate.priceType) {
      return affiliate.priceType
    }

    if (!market.isFilled()) {
      return PriceType.create({ value: 'gross' })
    }

    if (affiliate.isDefault() && market.is('us')) {
      return PriceType.create({
        value: PriceType.#USDefaultAffiliateFormat[market.value] ?? 'allIn',
      })
    }

    if (affiliate.isType('trivago')) {
      return PriceType.create({
        value: PriceType.#marketTrivagoPriceFormat[market.value] ?? 'allIn',
      })
    }

    if (affiliate.isType('tripadvisor')) {
      return PriceType.create({
        value: PriceType.#marketTripadvisorPriceFormat[market.value] ?? 'allInDaily',
      })
    }

    if (affiliate.isType('gha')) {
      return PriceType.create({
        value: PriceType.#marketGhaPriceFormat[market.value] ?? 'allInDaily',
      })
    }

    if (affiliate.isOneOfType(['findhotel', 'vio', 'skyscanner'])) {
      return PriceType.create({ value: 'allInDaily' })
    }

    if (affiliate.isOneOfType(['hotelscan', 'idealo']) && market.isOneOf(['mx', 'br'])) {
      return PriceType.create({ value: 'gross' })
    }

    if (affiliate.isType('kayak') && market.isOneOf(['it', 'nl', 'pt', 'uk', 'es', 'ca'])) {
      return PriceType.create({ value: 'allIn' })
    }

    if (affiliate.isType('kayak') && market.isOneOf(['us'])) {
      return PriceType.create({ value: 'netWithFees' })
    }

    if (market.isOneOf(PriceType.#marketToShowNightNetPrices)) {
      return PriceType.create({ value: 'net' })
    }

    if (market.isOneOf(PriceType.#marketToShowAllInPrices)) {
      return PriceType.create({ value: 'allIn' })
    }

    if (market.isOneOf(PriceType.#marketToShowAllInDailyPrices)) {
      return PriceType.create({ value: 'allInDaily' })
    }

    return PriceType.create({ value: 'gross' })
  }

  static fromJSON(json: ReturnType<PriceType['toJSON']>) {
    if (!json.value) {
      return PriceType.create({ value: 'gross' })
    }
    return PriceType.create({ value: json.value })
  }

  constructor(private readonly _value: PriceTypes) {
    super()
  }

  get value() { return this._value } // prettier-ignore

  get typeInformation() {
    return PriceType.priceTypesInformation[this.value]
  }

  getFeaturedPrice() {
    return this.typeInformation.featuredPrice
  }

  hasVats() {
    return this.typeInformation.vatsIncluded
  }

  hasTax() {
    return this.typeInformation.taxIncluded
  }

  hasFees() {
    return this.typeInformation.feesIncluded
  }

  isFeaturedPricePerNight() {
    return this.typeInformation.featuredPrice === 'perNight'
  }

  isFeaturedPriceTotal() {
    return this.typeInformation.featuredPrice === 'total'
  }

  toJSON() {
    return { value: this.value }
  }
}
